// src/components/SimpleComponents/SimpleTextArea.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef } from 'react';
import { Input } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import { useModuleContext } from '../core/ModuleProvider';
import utils from 'utils/utils';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { TextArea } = Input;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleTextArea = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  onBlur,

  useInitialInAvatar,
  useFirstInitialInAvatar,
  useSecondInitialInAvatar,

  repeatOnField,

  validateTrigger = 'onBlur',
  showFeedback = false,

  minLength,
  maxLength,

  rows,
  placeholder,

  autoFocus = false,

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, form, disabledFields } = useSimpleFormContext();
  const { setState, avatarInitialFirst, avatarInitialSecond } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Track last initials to avoid unnecessary re-renders
  const lastInitialsRef = useRef(null);
  const avatarDebounceTimerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (avatarDebounceTimerRef.current) {
        clearTimeout(avatarDebounceTimerRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const internalOnChanges = useCallback(
    (ev, ...restArgs) => {
      if (useInitialInAvatar || useFirstInitialInAvatar || useSecondInitialInAvatar) {
        const value = ev?.target?.value ?? ev ?? '';

        if (avatarDebounceTimerRef.current) {
          clearTimeout(avatarDebounceTimerRef.current);
        }

        // Debounce to handle rapid typing (100ms)
        avatarDebounceTimerRef.current = setTimeout(() => {
          const newInitials = utils.getNameInitial(value);

          // Only update if initials actually changed (avoids expensive re-render)
          if (newInitials !== lastInitialsRef.current) {
            lastInitialsRef.current = newInitials;
            applyAvatarInitials({
              value,
              mode: useInitialInAvatar ? 'all' : useFirstInitialInAvatar ? 'first' : 'second',
              setState,
              avatarInitialFirst,
              avatarInitialSecond,
            });
          }
        }, 100);
      }

      if (props.onChange) props.onChange(ev, ...restArgs);
    },
    [
      useInitialInAvatar,
      useFirstInitialInAvatar,
      useSecondInitialInAvatar,
      setState,
      avatarInitialFirst,
      avatarInitialSecond,
      props.onChange,
    ]
  );

  const { customValidationStatus, customValidationMessage, handleChange } =
    useCustomValidationStatus({
      ...props,
      minLength,
      maxLength,
      onChange: internalOnChanges,
      ...elementProps,
    });

  const handleBlur = useCallback(
    ev => {
      const value = ev?.target?.value ?? ev ?? '';

      if (useInitialInAvatar || useFirstInitialInAvatar || useSecondInitialInAvatar) {
        if (avatarDebounceTimerRef.current) {
          clearTimeout(avatarDebounceTimerRef.current);
          avatarDebounceTimerRef.current = null;
        }

        const newInitials = utils.getNameInitial(value);
        if (newInitials !== lastInitialsRef.current) {
          lastInitialsRef.current = newInitials;
          applyAvatarInitials({
            value,
            mode: useInitialInAvatar ? 'all' : useFirstInitialInAvatar ? 'first' : 'second',
            setState,
            avatarInitialFirst,
            avatarInitialSecond,
          });
        }
      }

      setTimeout(() => {
        // One-way copy on first blur if target is empty
        if (repeatOnField) {
          if (!form?.getFieldValue(repeatOnField)) {
            form?.setFieldValue(repeatOnField, value);
          }
        }

        onBlur?.(ev);
      }, 0);
    },
    [
      repeatOnField,
      form,
      onBlur,
      flattenFieldName,
      useInitialInAvatar,
      useFirstInitialInAvatar,
      useSecondInitialInAvatar,
      setState,
      avatarInitialFirst,
      avatarInitialSecond,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
    >
      <TextArea
        ref={elementRef}
        id={flattenFieldName}
        style={style}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={isDisabled}
        rows={rows}
        minLength={minLength}
        maxLength={maxLength}
        placeholder={translateX(placeholder)}
        autoFocus={autoFocus}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleTextArea;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function applyAvatarInitials({ value, mode, setState, avatarInitialFirst, avatarInitialSecond }) {
  // No avatar update requested or setState not available (e.g., in modal without ModuleProvider)
  if (!mode || typeof setState !== 'function') return;

  const v = value ?? '';

  if (mode === 'all') {
    setState({ avatarInitial: v });
    return;
  }

  if (mode === 'first') {
    setState({
      avatarInitial: `${v} ${avatarInitialSecond || ''}`.trim(),
      avatarInitialFirst: v,
    });
    return;
  }

  if (mode === 'second') {
    setState({
      avatarInitial: `${avatarInitialFirst || ''} ${v}`.trim(),
      avatarInitialSecond: v,
    });
  }
}
